import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Database, Search, RefreshCw, Users, ArrowRightLeft, CheckCircle2,
  ChevronLeft, ChevronRight, Archive, X, Loader2, History, Calendar,
  FileText, Wrench, Trash2, Pencil, RotateCcw, AlertTriangle,
  Shield, Eye, ChevronDown, Zap, Upload, AlertOctagon,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SortHeader = ({ field, sortBy, sortOrder, onSort, children, align = "left" }) => (
  <th
    className={`px-3 py-2.5 text-${align} text-[10px] font-semibold text-slate-500 uppercase cursor-pointer hover:text-slate-800 transition-colors select-none whitespace-nowrap`}
    onClick={() => onSort(field)}
    data-testid={`sort-${field}`}
  >
    <span className="inline-flex items-center gap-1">
      {children}
      {sortBy === field ? (
        <svg className={`w-3 h-3 transition-transform ${sortOrder === "asc" ? "" : "rotate-180"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>
      ) : (
        <svg className="w-3 h-3 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" /></svg>
      )}
    </span>
  </th>
);

// Small badge that shows which pricing-cascade tier set the task's rate.
const RATE_SOURCE_STYLES = {
  advanced:        { label: "ADV",  cls: "bg-violet-100 text-violet-700",   title: "Advanced multi-criteria rule" },
  resource_project:{ label: "R+P",  cls: "bg-indigo-100 text-indigo-700",   title: "Resource + Project specific rate" },
  project_builder: { label: "PROJ", cls: "bg-sky-100 text-sky-700",         title: "Project Price Builder rate" },
  individual:      { label: "IND",  cls: "bg-emerald-100 text-emerald-700", title: "Individual resource rate" },
  default:         { label: "DEF",  cls: "bg-slate-100 text-slate-600",     title: "Default task-type rate" },
};
const RateSourceBadge = ({ source, ruleId }) => {
  if (!source) return null;
  const s = RATE_SOURCE_STYLES[source];
  if (!s) return null;
  const title = ruleId ? `${s.title} • rule ${String(ruleId).slice(0, 8)}` : s.title;
  return (
    <span
      className={`inline-block text-[8px] font-semibold leading-none px-1 py-0.5 rounded border-0 ${s.cls}`}
      title={title}
      data-testid={`rate-source-${source}`}
    >
      {s.label}
    </span>
  );
};


const DatabaseAdjustments = () => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("active");

  // Active DB
  const [tasks, setTasks] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [filters, setFilters] = useState({ resources: [], task_types: [], languages: [] });
  const [loading, setLoading] = useState(true);

  // Original DB
  const [rawTasks, setRawTasks] = useState([]);
  const [rawTotal, setRawTotal] = useState(0);
  const [rawPage, setRawPage] = useState(1);
  const [rawPages, setRawPages] = useState(1);
  const [rawLoading, setRawLoading] = useState(false);

  const [stats, setStats] = useState(null);

  // Filters — initialize from URL params
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [resourceFilter, setResourceFilter] = useState(searchParams.get("resource") || "");
  const [taskTypeFilter, setTaskTypeFilter] = useState(searchParams.get("task_type") || "");
  const [languageFilter, setLanguageFilter] = useState(searchParams.get("language") || "");

  // Sort
  const [sortBy, setSortBy] = useState("due_date");
  const [sortOrder, setSortOrder] = useState("desc");

  // Selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);

  // Dialogs
  const [showWizard, setShowWizard] = useState(false);
  const [wizardTab, setWizardTab] = useState("resource");
  const [wizardNewValue, setWizardNewValue] = useState("");
  const [wizardFieldName, setWizardFieldName] = useState("amount");
  const [swapping, setSwapping] = useState(false);

  const [showAuditDialog, setShowAuditDialog] = useState(false);
  const [auditData, setAuditData] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);

  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showArchives, setShowArchives] = useState(false);
  const [archives, setArchives] = useState([]);
  const [deduplicating, setDeduplicating] = useState(false);
  const [showWipeDialog, setShowWipeDialog] = useState(false);
  const [wiping, setWiping] = useState(false);
  const [uploading, setUploading] = useState(false);
  const excelInputRef = useRef(null);

  // ── Fetch ────────────────────────────────────────────
  const fetchActive = useCallback(async (pg = page) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: pg, limit: 100, sort_by: sortBy, sort_order: sortOrder });
      if (search) params.set("search", search);
      if (resourceFilter) params.set("resource", resourceFilter);
      if (taskTypeFilter) params.set("task_type", taskTypeFilter);
      if (languageFilter) params.set("language", languageFilter);
      const [adjRes, statsRes] = await Promise.all([
        axios.get(`${API}/adjustments?${params}`),
        axios.get(`${API}/adjustments/comparison-stats`),
      ]);
      setTasks(adjRes.data.tasks || []);
      setTotal(adjRes.data.total);
      setPages(adjRes.data.pages);
      setFilters(adjRes.data.filters || { resources: [], task_types: [], languages: [] });
      setStats(statsRes.data);
      setSelectedIds(new Set());
      setSelectAll(false);
    } catch { toast.error("Failed to load data"); }
    finally { setLoading(false); }
  }, [search, resourceFilter, taskTypeFilter, languageFilter, sortBy, sortOrder, page]);

  const fetchRaw = useCallback(async (pg = rawPage) => {
    setRawLoading(true);
    try {
      const params = new URLSearchParams({ page: pg, limit: 100 });
      if (search) params.set("search", search);
      if (resourceFilter) params.set("resource", resourceFilter);
      if (taskTypeFilter) params.set("task_type", taskTypeFilter);
      const res = await axios.get(`${API}/raw-tasks?${params}`);
      setRawTasks(res.data.tasks || []);
      setRawTotal(res.data.total);
      setRawPages(res.data.pages);
    } catch { toast.error("Failed to load raw data"); }
    finally { setRawLoading(false); }
  }, [search, resourceFilter, taskTypeFilter, rawPage]);

  useEffect(() => {
    if (activeTab === "active") { fetchActive(1); setPage(1); }
    else { fetchRaw(1); setRawPage(1); }
  }, [search, resourceFilter, taskTypeFilter, languageFilter, sortBy, sortOrder, activeTab]);

  useEffect(() => {
    if (activeTab === "active") fetchActive(page);
    else fetchRaw(rawPage);
  }, [page, rawPage]);

  // ── Handlers ─────────────────────────────────────────
  const toggleSelect = (id) => {
    setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const toggleSelectAll = () => {
    if (selectAll) { setSelectedIds(new Set()); setSelectAll(false); }
    else { setSelectedIds(new Set(tasks.map(t => t.id))); setSelectAll(true); }
  };
  const toggleSort = (field) => {
    if (sortBy === field) setSortOrder(p => p === "asc" ? "desc" : "asc");
    else { setSortBy(field); setSortOrder("desc"); }
  };

  const handleWizardApply = async () => {
    if (!wizardNewValue || selectedIds.size === 0) return;
    setSwapping(true);
    try {
      let res;
      if (wizardTab === "status") {
        res = await axios.post(`${API}/adjustments/bulk-status`, { task_ids: Array.from(selectedIds), new_status: wizardNewValue });
        toast.success(`Updated status for ${res.data.updated} task(s)`);
      } else if (wizardTab === "field") {
        res = await axios.post(`${API}/adjustments/bulk-field-update`, { task_ids: Array.from(selectedIds), field: wizardFieldName, value: wizardNewValue });
        toast.success(`Updated ${wizardFieldName} for ${res.data.updated} task(s)`);
      } else {
        const endpoint = wizardTab === "resource" ? "resource-swap" : wizardTab === "task_type" ? "task-type-swap" : "due-date-swap";
        const fv = wizardTab === "due_date" && wizardNewValue.includes("-") ? wizardNewValue.split("-").reverse().join("/") : wizardNewValue;
        const body = wizardTab === "resource"
          ? { task_ids: Array.from(selectedIds), new_resource: fv }
          : { task_ids: Array.from(selectedIds), field: wizardTab === "task_type" ? "task" : "due_date", new_value: fv };
        res = await axios.post(`${API}/adjustments/${endpoint}`, body);
        toast.success(`Updated ${res.data.swapped} task(s)`);
      }
      setShowWizard(false); setWizardNewValue(""); setSelectedIds(new Set()); fetchActive(page);
    } catch (err) { toast.error(err.response?.data?.detail || "Update failed"); }
    finally { setSwapping(false); }
  };

  const fetchAuditTrail = async (taskId) => {
    setAuditLoading(true);
    try { const res = await axios.get(`${API}/adjustments/${taskId}/history`); setAuditData(res.data); setShowAuditDialog(true); }
    catch { toast.error("Failed to load history"); }
    finally { setAuditLoading(false); }
  };

  const openEditDialog = (task) => {
    if (task.invoice_id) {
      const ok = window.confirm(
        `⚠️ This task is already INVOICED on ${task.invoice_number || "an invoice"}.\n\n` +
        `Editing will diverge live data from the issued invoice. Continue only if absolutely necessary (e.g., correcting a typo).\n\n` +
        `Open editor?`
      );
      if (!ok) return;
    } else if (task.batch_id) {
      const ok = window.confirm(
        `This task belongs to a LOCKED invoice batch.\n\n` +
        `Edits will be reflected in analytics but the locked batch keeps its snapshot. Mismatches will be flagged before invoice generation.\n\n` +
        `Continue editing?`
      );
      if (!ok) return;
    }
    setEditTask(task);
    setEditForm({
      main_title: task.main_title || "", episode_title: task.episode_title || "",
      season_number: task.season_number || "", episode_number: task.episode_number || "",
      task: task.task || "", resource: task.resource || "", language: task.language || "",
      due_date: task.due_date || "", duration_words: task.duration_words || 0,
      amount: task.amount || 0, task_status: task.task_status || "",
    });
    setShowEditDialog(true);
  };
  const handleEditSave = async (force = false) => {
    if (!editTask) return;
    setEditSaving(true);
    try {
      const url = `${API}/adjustments/record/${editTask.id}${force ? "?force=true" : ""}`;
      const res = await axios.put(url, { fields: editForm });
      toast.success(`Updated ${res.data.changed} field(s)`);
      setShowEditDialog(false);
      fetchActive(page);
    } catch (err) {
      const detail = err.response?.data?.detail;
      // 409: task is invoiced — ask user to confirm override
      if (err.response?.status === 409 && detail?.error === "task_invoiced") {
        const ok = window.confirm(`${detail.message}\n\nProceed anyway? Live data will diverge from invoice ${detail.invoice_number}.`);
        if (ok) { setEditSaving(false); return handleEditSave(true); }
      } else {
        toast.error(typeof detail === "string" ? detail : (detail?.message || "Update failed"));
      }
    } finally { setEditSaving(false); }
  };

  const handleBulkDelete = async () => {
    setDeleting(true);
    try { const res = await axios.post(`${API}/adjustments/bulk-delete`, { task_ids: Array.from(selectedIds) }); toast.success(`Deleted ${res.data.deleted} record(s)`); setShowDeleteConfirm(false); setSelectedIds(new Set()); fetchActive(page); }
    catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
    finally { setDeleting(false); }
  };
  const handleSingleDelete = async (taskId) => {
    try { await axios.delete(`${API}/adjustments/record/${taskId}`); toast.success("Record deleted"); fetchActive(page); }
    catch { toast.error("Delete failed"); }
  };

  const handleReset = async () => {
    setResetting(true);
    try { const res = await axios.post(`${API}/adjustments/reset-to-original`); toast.success(`Reset: ${res.data.new_count} records restored`); setShowResetDialog(false); fetchActive(1); setPage(1); }
    catch (err) { toast.error(err.response?.data?.detail || "Reset failed"); }
    finally { setResetting(false); }
  };

  const handleDeduplicate = async () => {
    setDeduplicating(true);
    try {
      const res = await axios.post(`${API}/adjustments/deduplicate`);
      if (res.data.removed > 0) {
        toast.success(`Removed ${res.data.removed} duplicate records. ${res.data.remaining} remaining.`);
        fetchActive(1); setPage(1);
      } else {
        toast.info("No duplicates found.");
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Deduplication failed"); }
    finally { setDeduplicating(false); }
  };

  const fetchArchives = async () => {
    try { const res = await axios.get(`${API}/sync-archives`); setArchives(res.data.archives || []); }
    catch { toast.error("Failed to load archives"); }
  };

  const handleWipeAll = async () => {
    setWiping(true);
    try {
      const res = await axios.delete(`${API}/clear-database`);
      toast.success("All data cleared. Upload fresh Excel to start over.");
      setShowWipeDialog(false);
      fetchActive(1); setPage(1);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to clear database"); }
    finally { setWiping(false); }
  };

  const handleExcelUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      // Upload tasks sheet
      const res = await axios.post(`${API}/upload-tasks`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded ${res.data.tasks_imported || 0} tasks. Now creating working copy...`);
      // Auto-create adjustments copy
      const copyRes = await axios.post(`${API}/adjustments/create-copy`);
      toast.success(`Working copy created: ${copyRes.data.new_copied} tasks synced.`);
      fetchActive(1); setPage(1);
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); if (excelInputRef.current) excelInputRef.current.value = ""; }
  };

  const clearFilters = () => { setSearch(""); setResourceFilter(""); setTaskTypeFilter(""); setLanguageFilter(""); };
  const hasFilters = search || resourceFilter || taskTypeFilter || languageFilter;

  // ── Empty state ──────────────────────────────────────
  if (!loading && stats && !stats.adjustments.count && activeTab === "active") {
    return (
      <div className="p-6" data-testid="adjustments-empty">
        <div className="max-w-lg mx-auto text-center py-24">
          <Database className="w-16 h-16 mx-auto mb-4 text-slate-300" />
          <h2 className="text-xl font-bold text-slate-800 mb-2">No Active Database</h2>
          <p className="text-sm text-slate-500 mb-6">Upload a fresh Excel file to import tasks and create the working database.</p>
          <div className="flex items-center justify-center gap-3">
            <Button onClick={() => excelInputRef.current?.click()} disabled={uploading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="empty-upload-btn">
              {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
              {uploading ? "Uploading..." : "Upload Excel"}
            </Button>
            <input ref={excelInputRef} type="file" accept=".xlsx,.xls" onChange={handleExcelUpload} className="hidden" />
            <Button variant="outline" onClick={() => window.location.href = "/sync"} data-testid="go-to-sync-btn"><Database className="w-4 h-4 mr-1" /> Go to Database Sync</Button>
          </div>
        </div>

        {/* Wipe Dialog still needed in empty state */}
        <Dialog open={showWipeDialog} onOpenChange={setShowWipeDialog}>
          <DialogContent className="max-w-sm" data-testid="wipe-confirm-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600"><AlertOctagon className="w-5 h-5" /> Clear All Data</DialogTitle>
              <DialogDescription>This will permanently delete ALL data including tasks, invoices, batches, etc.</DialogDescription>
            </DialogHeader>
            <div className="flex gap-3 pt-2">
              <Button className="flex-1 bg-red-600 hover:bg-red-700" onClick={handleWipeAll} disabled={wiping} data-testid="confirm-wipe-btn">
                {wiping ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
                {wiping ? "Clearing..." : "Clear Everything"}
              </Button>
              <Button variant="outline" onClick={() => setShowWipeDialog(false)} className="flex-1">Cancel</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  const curTotal = activeTab === "active" ? total : rawTotal;
  const curPage = activeTab === "active" ? page : rawPage;
  const curPages = activeTab === "active" ? pages : rawPages;

  // ═══ RENDER ══════════════════════════════════════════
  return (
    <div className="p-5 space-y-3" data-testid="adjustments-page">
      {/* ── HEADER ── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h1 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Poppins, system-ui' }}>Database Center</h1>

          {/* Active DB indicator */}
          <div className="flex items-center bg-slate-100 rounded-lg p-0.5">
            <button
              onClick={() => setActiveTab("active")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${activeTab === "active" ? "bg-white text-emerald-700 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
              data-testid="switch-to-active"
            >
              <Database className="w-3.5 h-3.5" />
              Active
              {stats && <span className="text-[10px] opacity-70">({stats.adjustments.count.toLocaleString()})</span>}
            </button>
            <button
              onClick={() => setActiveTab("original")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${activeTab === "original" ? "bg-white text-blue-700 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
              data-testid="switch-to-original"
            >
              <Shield className="w-3.5 h-3.5" />
              Original
              {stats && <span className="text-[10px] opacity-70">({stats.raw.count.toLocaleString()})</span>}
            </button>
          </div>

          {stats && !stats.in_sync && (
            <Badge className="bg-amber-50 text-amber-700 border-0 text-[10px]">{stats.adjustments.edited} edited</Badge>
          )}
          {stats && stats.in_sync && (
            <Badge className="bg-emerald-50 text-emerald-700 border-0 text-[10px]">In Sync</Badge>
          )}
        </div>

        {/* Operations buttons */}
        <div className="flex items-center gap-1.5">
          {/* Upload Fresh Data */}
          <Button variant="outline" size="sm" className="h-8 text-xs text-emerald-600 border-emerald-200 hover:bg-emerald-50" onClick={() => excelInputRef.current?.click()} disabled={uploading} data-testid="upload-excel-btn">
            {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
            {uploading ? "Uploading..." : "Upload Excel"}
          </Button>
          <input ref={excelInputRef} type="file" accept=".xlsx,.xls" onChange={handleExcelUpload} className="hidden" />

          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => { fetchArchives(); setShowArchives(true); }} data-testid="archives-btn">
            <Archive className="w-3.5 h-3.5 mr-1" /> Archives {stats ? `(${stats.archives})` : ""}
          </Button>
          <Button variant="outline" size="sm" className="h-8 text-xs text-blue-600 border-blue-200 hover:bg-blue-50" onClick={handleDeduplicate} disabled={deduplicating} data-testid="deduplicate-btn">
            {deduplicating ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Zap className="w-3.5 h-3.5 mr-1" />}
            {deduplicating ? "Deduplicating..." : "Deduplicate"}
          </Button>
          <Button variant="outline" size="sm" className="h-8 text-xs text-amber-600 border-amber-200 hover:bg-amber-50" onClick={() => setShowResetDialog(true)} data-testid="reset-btn">
            <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset
          </Button>
          <Button variant="outline" size="sm" className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50" onClick={() => setShowWipeDialog(true)} data-testid="wipe-all-btn">
            <AlertOctagon className="w-3.5 h-3.5 mr-1" /> Clear All
          </Button>
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => activeTab === "active" ? fetchActive(page) : fetchRaw(rawPage)} data-testid="refresh-btn">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {/* Read-only banner */}
      {activeTab === "original" && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 flex items-center gap-2">
          <Shield className="w-4 h-4 text-blue-600" />
          <span className="text-xs font-medium text-blue-800">Read-Only — This is the original uploaded data</span>
          <Button variant="ghost" size="sm" className="ml-auto h-6 text-xs text-blue-600" onClick={() => setActiveTab("active")}>
            <Eye className="w-3 h-3 mr-1" /> Switch to Active
          </Button>
        </div>
      )}

      {/* ── FILTERS ── */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <Input placeholder="Search project, episode, resource..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-8 text-xs" data-testid="adj-search" />
        </div>
        <Select value={resourceFilter} onValueChange={v => setResourceFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-48 h-8 text-xs" data-testid="adj-resource-filter"><SelectValue placeholder="All Resources" /></SelectTrigger>
          <SelectContent className="max-h-64">
            <SelectItem value="all">All Resources</SelectItem>
            {filters.resources.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={taskTypeFilter} onValueChange={v => setTaskTypeFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-36 h-8 text-xs" data-testid="adj-task-filter"><SelectValue placeholder="All Tasks" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tasks</SelectItem>
            {filters.task_types.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        {activeTab === "active" && (
          <Select value={languageFilter} onValueChange={v => setLanguageFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-36 h-8 text-xs" data-testid="adj-lang-filter"><SelectValue placeholder="All Languages" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Languages</SelectItem>
              {filters.languages.map(lg => <SelectItem key={lg} value={lg}>{lg}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="text-[10px] text-slate-500 h-8 px-2">
            <X className="w-3 h-3 mr-1" /> Clear
          </Button>
        )}
      </div>

      {/* ── ACTION BAR (Active only) ── */}
      {activeTab === "active" && selectedIds.size > 0 && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-2 flex items-center justify-between" data-testid="adj-action-bar">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-indigo-600" />
            <span className="text-xs font-semibold text-indigo-800">{selectedIds.size} selected</span>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" className="h-7 text-xs" onClick={() => { setWizardTab("resource"); setWizardNewValue(""); setShowWizard(true); }} data-testid="new-adjustment-btn">
              <Wrench className="w-3.5 h-3.5 mr-1" /> Adjust
            </Button>
            <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => { setWizardTab("status"); setWizardNewValue(""); setShowWizard(true); }} data-testid="bulk-status-btn">
              <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Status
            </Button>
            <Button size="sm" className="h-7 text-xs bg-blue-600 hover:bg-blue-700" onClick={() => { setWizardTab("field"); setWizardNewValue(""); setShowWizard(true); }} data-testid="bulk-field-btn">
              <Pencil className="w-3.5 h-3.5 mr-1" /> Edit Field
            </Button>
            <Button size="sm" variant="destructive" className="h-7 text-xs" onClick={() => setShowDeleteConfirm(true)} data-testid="bulk-delete-btn">
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
            </Button>
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => { setSelectedIds(new Set()); setSelectAll(false); }}>Clear</Button>
          </div>
        </div>
      )}

      {/* ═══ ACTIVE TABLE ═══ */}
      {activeTab === "active" && (
        <Card className="border border-slate-200">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-20"><div className="w-7 h-7 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" /></div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="adj-table">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-2 py-2 w-8"><Checkbox checked={selectAll} onCheckedChange={toggleSelectAll} data-testid="adj-select-all" /></th>
                        <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-500">#</th>
                        <SortHeader field="main_title" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort}>Project</SortHeader>
                        <SortHeader field="season_number" align="center" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort}>S/Ep</SortHeader>
                        <SortHeader field="episode_title" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort}>Episode</SortHeader>
                        <SortHeader field="task" align="center" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort}>Task</SortHeader>
                        <SortHeader field="resource" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort}>Resource</SortHeader>
                        <SortHeader field="language" align="center" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort}>Lang</SortHeader>
                        <SortHeader field="due_date" align="center" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort}>Due Date</SortHeader>
                        <SortHeader field="duration_words" align="center" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort}>Dur</SortHeader>
                        <SortHeader field="amount" align="right" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort}>Amount</SortHeader>
                        <SortHeader field="task_status" align="center" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort}>Status</SortHeader>
                        <th className="px-2 py-2 text-center text-[10px] font-semibold text-slate-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tasks.map((t, idx) => {
                        const isSel = selectedIds.has(t.id);
                        const edits = (t.adjustment_log || []).length;
                        const isInvoiced = !!t.invoice_id;
                        const isLocked = !!t.batch_id;
                        return (
                          <tr key={t.id || idx} className={`border-b border-slate-100 transition-colors ${isSel ? "bg-indigo-50/60" : isInvoiced ? "bg-violet-50/30 hover:bg-violet-50/50" : isLocked ? "bg-emerald-50/20 hover:bg-emerald-50/40" : "hover:bg-slate-50/50"}`} data-testid={`adj-row-${idx}`}>
                            <td className="px-2 py-1.5"><Checkbox checked={isSel} onCheckedChange={() => toggleSelect(t.id)} /></td>
                            <td className="px-2 py-1.5 text-[10px] text-slate-400">{(page - 1) * 100 + idx + 1}</td>
                            <td className="px-2 py-1.5 text-[11px] font-medium text-slate-700 max-w-[180px]">
                              <div className="flex items-center gap-1">
                                <span className="truncate">{t.main_title || "-"}</span>
                                {isInvoiced && <Badge className="bg-violet-100 text-violet-700 border-0 text-[8px] px-1 py-0 shrink-0" title={`Invoice ${t.invoice_number || ""}`}>INVOICED</Badge>}
                                {isLocked && !isInvoiced && <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[8px] px-1 py-0 shrink-0" title="Part of a locked invoice batch">LOCKED</Badge>}
                              </div>
                            </td>
                            <td className="px-2 py-1.5 text-center text-[10px] text-slate-600 whitespace-nowrap" data-testid={`adj-season-ep-${idx}`}>
                              {(t.season_number || t.episode_number)
                                ? <span className="font-mono">{t.season_number ? `S${String(t.season_number).replace(/\.0$/, "")}` : "-"}{t.episode_number ? ` · E${String(t.episode_number).replace(/\.0$/, "")}` : ""}</span>
                                : <span className="text-slate-300">-</span>}
                            </td>
                            <td className="px-2 py-1.5 text-[11px] text-slate-600 max-w-[110px] truncate">{t.episode_title || "-"}</td>
                            <td className="px-2 py-1.5 text-center"><Badge className="bg-blue-50 text-blue-700 border-0 text-[9px]">{t.task || "-"}</Badge></td>
                            <td className="px-2 py-1.5 text-[11px] text-slate-700 max-w-[140px] truncate">{t.resource || "-"}</td>
                            <td className="px-2 py-1.5 text-center text-[10px] text-slate-600">{t.language || "-"}</td>
                            <td className="px-2 py-1.5 text-center text-[10px] text-slate-500 whitespace-nowrap">{t.due_date || "-"}</td>
                            <td className="px-2 py-1.5 text-center text-[11px] text-slate-600">{t.duration_words || 0}</td>
                            <td className="px-2 py-1.5 text-right text-[11px] font-semibold text-slate-700">
                              <div className="flex items-center justify-end gap-1">
                                <RateSourceBadge source={t.rate_source} ruleId={t.rate_rule_id} />
                                <span>{"\u20B9"}{(t.amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
                              </div>
                            </td>
                            <td className="px-2 py-1.5 text-center">
                              <Badge className={`border-0 text-[9px] ${t.task_status?.toLowerCase() === 'completed' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                                {t.task_status || "In Progress"}
                              </Badge>
                            </td>
                            <td className="px-2 py-1.5">
                              <div className="flex items-center justify-center gap-0.5">
                                <button onClick={() => openEditDialog(t)} className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-blue-600" title="Edit" data-testid={`edit-btn-${idx}`}><Pencil className="w-3 h-3" /></button>
                                <button onClick={() => fetchAuditTrail(t.id)} className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-amber-600 relative" title="History" data-testid={`audit-btn-${idx}`}>
                                  <History className="w-3 h-3" />
                                  {edits > 0 && <span className="absolute -top-0.5 -right-0.5 bg-amber-500 text-white text-[7px] rounded-full w-3 h-3 flex items-center justify-center leading-none">{edits}</span>}
                                </button>
                                <button onClick={() => handleSingleDelete(t.id)} className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-600" title="Delete" data-testid={`delete-btn-${idx}`}><Trash2 className="w-3 h-3" /></button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {tasks.length === 0 && <tr><td colSpan={13} className="py-16 text-center text-sm text-slate-400">No records found</td></tr>}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center justify-between px-4 py-2.5 border-t border-slate-200">
                  <p className="text-[10px] text-slate-500">Showing {(page - 1) * 100 + 1}–{Math.min(page * 100, total)} of {total}</p>
                  <div className="flex items-center gap-1.5">
                    <Button variant="outline" size="sm" className="h-7" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="adj-prev-page"><ChevronLeft className="w-3.5 h-3.5" /></Button>
                    <span className="text-[10px] text-slate-600">{page}/{pages}</span>
                    <Button variant="outline" size="sm" className="h-7" disabled={page >= pages} onClick={() => setPage(p => p + 1)} data-testid="adj-next-page"><ChevronRight className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* ═══ ORIGINAL TABLE ═══ */}
      {activeTab === "original" && (
        <Card className="border border-blue-200">
          <CardContent className="p-0">
            {rawLoading ? (
              <div className="flex items-center justify-center py-20"><div className="w-7 h-7 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" /></div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="raw-table">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-500">#</th>
                        <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-500">Project</th>
                        <th className="px-2 py-2 text-center text-[10px] font-semibold text-slate-500">S/Ep</th>
                        <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-500">Episode</th>
                        <th className="px-2 py-2 text-center text-[10px] font-semibold text-slate-500">Task</th>
                        <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-500">Resource</th>
                        <th className="px-2 py-2 text-center text-[10px] font-semibold text-slate-500">Language</th>
                        <th className="px-2 py-2 text-center text-[10px] font-semibold text-slate-500">Due Date</th>
                        <th className="px-2 py-2 text-center text-[10px] font-semibold text-slate-500">Duration</th>
                        <th className="px-2 py-2 text-right text-[10px] font-semibold text-slate-500">Amount</th>
                        <th className="px-2 py-2 text-center text-[10px] font-semibold text-slate-500">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rawTasks.map((t, idx) => (
                        <tr key={t.id || idx} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`raw-row-${idx}`}>
                          <td className="px-2 py-1.5 text-[10px] text-slate-400">{(rawPage - 1) * 100 + idx + 1}</td>
                          <td className="px-2 py-1.5 text-[11px] font-medium text-slate-700 max-w-[140px] truncate">{t.main_title || "-"}</td>
                          <td className="px-2 py-1.5 text-center text-[10px] text-slate-600 whitespace-nowrap">
                            {(t.season_number || t.episode_number)
                              ? <span className="font-mono">{t.season_number ? `S${String(t.season_number).replace(/\.0$/, "")}` : "-"}{t.episode_number ? ` · E${String(t.episode_number).replace(/\.0$/, "")}` : ""}</span>
                              : <span className="text-slate-300">-</span>}
                          </td>
                          <td className="px-2 py-1.5 text-[11px] text-slate-600 max-w-[110px] truncate">{t.episode_title || "-"}</td>
                          <td className="px-2 py-1.5 text-center"><Badge className="bg-blue-50 text-blue-700 border-0 text-[9px]">{t.task || "-"}</Badge></td>
                          <td className="px-2 py-1.5 text-[11px] text-slate-700 max-w-[140px] truncate">{t.resource || "-"}</td>
                          <td className="px-2 py-1.5 text-center text-[10px] text-slate-600">{t.language || "-"}</td>
                          <td className="px-2 py-1.5 text-center text-[10px] text-slate-500">{t.due_date || "-"}</td>
                          <td className="px-2 py-1.5 text-center text-[11px] text-slate-600">{t.duration_words || 0}</td>
                          <td className="px-2 py-1.5 text-right text-[11px] font-semibold text-slate-700">{"\u20B9"}{(t.amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                          <td className="px-2 py-1.5 text-center">
                            <Badge className={`border-0 text-[9px] ${t.task_status?.toLowerCase() === 'completed' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>{t.task_status || "In Progress"}</Badge>
                          </td>
                        </tr>
                      ))}
                      {rawTasks.length === 0 && <tr><td colSpan={11} className="py-16 text-center text-sm text-slate-400">No records found</td></tr>}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center justify-between px-4 py-2.5 border-t border-slate-200">
                  <p className="text-[10px] text-slate-500">Showing {(rawPage - 1) * 100 + 1}–{Math.min(rawPage * 100, rawTotal)} of {rawTotal}</p>
                  <div className="flex items-center gap-1.5">
                    <Button variant="outline" size="sm" className="h-7" disabled={rawPage <= 1} onClick={() => setRawPage(p => p - 1)} data-testid="raw-prev-page"><ChevronLeft className="w-3.5 h-3.5" /></Button>
                    <span className="text-[10px] text-slate-600">{rawPage}/{rawPages}</span>
                    <Button variant="outline" size="sm" className="h-7" disabled={rawPage >= rawPages} onClick={() => setRawPage(p => p + 1)} data-testid="raw-next-page"><ChevronRight className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* ═══ DIALOGS ═══ */}

      {/* Wizard */}
      <Dialog open={showWizard} onOpenChange={setShowWizard}>
        <DialogContent className="max-w-lg" data-testid="wizard-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Wrench className="w-5 h-5 text-indigo-600" /> New Adjustment</DialogTitle>
            <DialogDescription>Apply changes to {selectedIds.size} selected task(s)</DialogDescription>
          </DialogHeader>
          <div className="flex gap-1 bg-slate-100 p-1 rounded-lg flex-wrap">
            {[{ key: "resource", icon: Users, label: "Resource" }, { key: "task_type", icon: FileText, label: "Task Type" }, { key: "due_date", icon: Calendar, label: "Due Date" }, { key: "status", icon: CheckCircle2, label: "Status" }, { key: "field", icon: Pencil, label: "Edit Field" }].map(tab => (
              <button key={tab.key} className={`flex-1 text-xs py-2 px-2 rounded-md font-medium transition-colors ${wizardTab === tab.key ? "bg-white text-indigo-700 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                onClick={() => { setWizardTab(tab.key); setWizardNewValue(""); }} data-testid={`wizard-tab-${tab.key}`}>
                <tab.icon className="w-3.5 h-3.5 inline mr-1" /> {tab.label}
              </button>
            ))}
          </div>
          <div className="space-y-4 pt-2">
            {wizardTab === "resource" && (
              <div data-testid="wizard-resource-panel">
                <label className="text-xs font-medium text-slate-600 mb-1.5 block">New Resource Email</label>
                <Select value={wizardNewValue} onValueChange={setWizardNewValue}>
                  <SelectTrigger data-testid="wizard-resource-select"><SelectValue placeholder="Select resource..." /></SelectTrigger>
                  <SelectContent className="max-h-64">{filters.resources.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
                <p className="text-[10px] text-slate-400 mt-1.5">Or type a new email:</p>
                <Input value={wizardNewValue} onChange={e => setWizardNewValue(e.target.value)} placeholder="resource@email.com" className="mt-1 h-8 text-sm" data-testid="wizard-resource-input" />
              </div>
            )}
            {wizardTab === "task_type" && (
              <div data-testid="wizard-tasktype-panel">
                <label className="text-xs font-medium text-slate-600 mb-1.5 block">New Task Type</label>
                <Select value={wizardNewValue} onValueChange={setWizardNewValue}>
                  <SelectTrigger data-testid="wizard-tasktype-select"><SelectValue placeholder="Select task type..." /></SelectTrigger>
                  <SelectContent>{filters.task_types.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {wizardTab === "due_date" && (
              <div data-testid="wizard-duedate-panel">
                <label className="text-xs font-medium text-slate-600 mb-1.5 block">New Due Date</label>
                <Input type="date" value={wizardNewValue} onChange={e => setWizardNewValue(e.target.value)} className="h-9 text-sm" data-testid="wizard-duedate-input" />
              </div>
            )}
            {wizardTab === "status" && (
              <div data-testid="wizard-status-panel">
                <label className="text-xs font-medium text-slate-600 mb-1.5 block">New Status</label>
                <Select value={wizardNewValue} onValueChange={setWizardNewValue}>
                  <SelectTrigger data-testid="wizard-status-select"><SelectValue placeholder="Select status..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Completed">Completed</SelectItem>
                    <SelectItem value="In Progress">In Progress</SelectItem>
                    <SelectItem value="Pending">Pending</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {wizardTab === "field" && (
              <div data-testid="wizard-field-panel" className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1.5 block">Field to Update</label>
                  <Select value={wizardFieldName} onValueChange={v => { setWizardFieldName(v); setWizardNewValue(""); }}>
                    <SelectTrigger data-testid="wizard-field-name"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="amount">Amount</SelectItem>
                      <SelectItem value="duration_words">Duration/Words</SelectItem>
                      <SelectItem value="rate">Rate</SelectItem>
                      <SelectItem value="language">Language</SelectItem>
                      <SelectItem value="main_title">Project Title</SelectItem>
                      <SelectItem value="episode_title">Episode Title</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1.5 block">New Value</label>
                  {["amount", "duration_words", "rate"].includes(wizardFieldName)
                    ? <Input type="number" value={wizardNewValue} onChange={e => setWizardNewValue(e.target.value)} placeholder="Enter number..." className="h-9 text-sm" data-testid="wizard-field-value" />
                    : wizardFieldName === "language"
                    ? <Select value={wizardNewValue} onValueChange={setWizardNewValue}><SelectTrigger data-testid="wizard-field-value"><SelectValue placeholder="Select..." /></SelectTrigger><SelectContent>{filters.languages.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent></Select>
                    : <Input value={wizardNewValue} onChange={e => setWizardNewValue(e.target.value)} placeholder="Enter value..." className="h-9 text-sm" data-testid="wizard-field-value" />
                  }
                </div>
              </div>
            )}
            <div className="flex gap-3 pt-1">
              <Button onClick={handleWizardApply} disabled={!wizardNewValue || swapping} className="flex-1" data-testid="wizard-apply-btn">
                {swapping ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ArrowRightLeft className="w-4 h-4 mr-1" />}
                {swapping ? "Applying..." : `Apply to ${selectedIds.size} Task(s)`}
              </Button>
              <Button variant="outline" onClick={() => setShowWizard(false)} className="flex-1">Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Audit Trail */}
      <Dialog open={showAuditDialog} onOpenChange={setShowAuditDialog}>
        <DialogContent className="max-w-lg" data-testid="audit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><History className="w-5 h-5 text-amber-600" /> Audit Trail</DialogTitle>
            {auditData && <DialogDescription>{auditData.main_title}{auditData.episode_title ? ` — ${auditData.episode_title}` : ""}</DialogDescription>}
          </DialogHeader>
          {auditData && (
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-200 text-xs space-y-1">
              <div className="flex justify-between"><span className="text-slate-500">Resource:</span><span className="font-medium text-slate-700">{auditData.resource || "-"}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Task Type:</span><span className="font-medium text-slate-700">{auditData.task || "-"}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Due Date:</span><span className="font-medium text-slate-700">{auditData.due_date || "-"}</span></div>
            </div>
          )}
          <div className="space-y-2 pt-1 max-h-[350px] overflow-y-auto">
            {auditLoading ? (
              <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : auditData?.adjustment_log?.length > 0 ? (
              [...auditData.adjustment_log].reverse().map((entry, i) => (
                <div key={i} className="p-3 bg-white rounded-lg border border-slate-200">
                  <div className="flex items-center justify-between mb-1.5">
                    <Badge className={`border-0 text-[10px] ${entry.field === "resource" ? "bg-blue-50 text-blue-700" : entry.field === "task" ? "bg-purple-50 text-purple-700" : "bg-amber-50 text-amber-700"}`}>
                      {entry.action?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) || entry.field}
                    </Badge>
                    <span className="text-[10px] text-slate-400">{new Date(entry.timestamp).toLocaleString()}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-red-500 bg-red-50 px-1.5 py-0.5 rounded line-through">{entry.old_value || "(empty)"}</span>
                    <ArrowRightLeft className="w-3 h-3 text-slate-400 flex-shrink-0" />
                    <span className="text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded font-medium">{entry.new_value}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8"><History className="w-8 h-8 mx-auto text-slate-200 mb-2" /><p className="text-sm text-slate-400">No adjustments recorded</p></div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Record */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md" data-testid="edit-record-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Pencil className="w-5 h-5 text-blue-600" /> Edit Record</DialogTitle>
            <DialogDescription>Changes are tracked in the audit trail</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2 max-h-[400px] overflow-y-auto">
            {[
              { key: "main_title", label: "Project" }, { key: "episode_title", label: "Episode" },
              { key: "season_number", label: "Season" }, { key: "episode_number", label: "Episode #" },
              { key: "task", label: "Task Type" }, { key: "resource", label: "Resource" },
              { key: "language", label: "Language" }, { key: "due_date", label: "Due Date" },
              { key: "duration_words", label: "Duration/Words", type: "number" },
              { key: "amount", label: "Amount", type: "number" }, { key: "task_status", label: "Status" },
            ].map(f => (
              <div key={f.key}>
                <label className="text-[10px] font-medium text-slate-500 uppercase mb-1 block">{f.label}</label>
                <Input type={f.type || "text"} value={editForm[f.key] ?? ""} onChange={e => setEditForm(prev => ({ ...prev, [f.key]: f.type === "number" ? Number(e.target.value) : e.target.value }))} className="h-8 text-sm" data-testid={`edit-field-${f.key}`} />
              </div>
            ))}
          </div>
          <div className="flex gap-3 pt-2">
            <Button onClick={handleEditSave} disabled={editSaving} className="flex-1" data-testid="edit-save-btn">
              {editSaving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
              {editSaving ? "Saving..." : "Save Changes"}
            </Button>
            <Button variant="outline" onClick={() => setShowEditDialog(false)} className="flex-1">Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="max-w-sm" data-testid="delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600"><Trash2 className="w-5 h-5" /> Delete Records</DialogTitle>
            <DialogDescription>Delete {selectedIds.size} record(s) from the active database? This cannot be undone.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 pt-2">
            <Button variant="destructive" onClick={handleBulkDelete} disabled={deleting} className="flex-1" data-testid="confirm-delete-btn">
              {deleting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
              {deleting ? "Deleting..." : `Delete ${selectedIds.size}`}
            </Button>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)} className="flex-1">Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset */}
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent className="max-w-sm" data-testid="reset-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600"><AlertTriangle className="w-5 h-5" /> Reset to Original</DialogTitle>
            <DialogDescription>This will wipe all adjustments and re-copy from the original database. All edits will be lost. Current state is archived first.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 pt-2">
            <Button className="flex-1 bg-amber-600 hover:bg-amber-700" onClick={handleReset} disabled={resetting} data-testid="confirm-reset-btn">
              {resetting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1" />}
              {resetting ? "Resetting..." : "Reset Now"}
            </Button>
            <Button variant="outline" onClick={() => setShowResetDialog(false)} className="flex-1">Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Archives */}
      <Dialog open={showArchives} onOpenChange={setShowArchives}>
        <DialogContent className="max-w-lg" data-testid="archives-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Archive className="w-5 h-5 text-amber-600" /> Archives</DialogTitle>
            <DialogDescription>Snapshots from sync operations and resets</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 pt-2 max-h-[400px] overflow-y-auto">
            {archives.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-8">No archives yet</p>
            ) : archives.map((a, i) => (
              <div key={a.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                <div>
                  <p className="text-sm font-medium text-slate-700">{a.action === "reset_to_original" ? "Reset" : `Sync #${archives.length - i}`}</p>
                  <p className="text-[10px] text-slate-400">{new Date(a.created_at).toLocaleString()} — {a.task_count} tasks</p>
                </div>
                <Badge className={`border-0 text-xs ${a.action === "reset_to_original" ? "bg-amber-50 text-amber-600" : "bg-slate-100 text-slate-600"}`}>
                  {a.action === "reset_to_original" ? "Reset" : `${a.new_tasks_copied} copied`}
                </Badge>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Wipe All Confirmation */}
      <Dialog open={showWipeDialog} onOpenChange={setShowWipeDialog}>
        <DialogContent className="max-w-sm" data-testid="wipe-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600"><AlertOctagon className="w-5 h-5" /> Clear All Data</DialogTitle>
            <DialogDescription>This will permanently delete ALL data: tasks, adjustments, invoices, batches, assignments, and archives. This cannot be undone. You can then upload a fresh Excel file.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 pt-2">
            <Button className="flex-1 bg-red-600 hover:bg-red-700" onClick={handleWipeAll} disabled={wiping} data-testid="confirm-wipe-btn">
              {wiping ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
              {wiping ? "Clearing..." : "Clear Everything"}
            </Button>
            <Button variant="outline" onClick={() => setShowWipeDialog(false)} className="flex-1">Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DatabaseAdjustments;
